
// @/components/themes/RelatedProducts.tsx
"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

import Link from "next/link";
import { useRef, useState, useEffect } from "react";
import { Card, CardContent } from "../themes/ui/card";
import { Star } from "lucide-react";

import { Product } from "@/types/product";
import { axiosGetOverallProductRatings } from "@/utilities/axioscall";
import { getAssetsBaseUrl, getCurrencySymbol } from "@/services/storeService";

const assets = `${getAssetsBaseUrl()}`;

interface RelatedProductsProps {
  allProducts: Product[];
  currentCategoryId: string;
  currentProductId: string;
}

export default function RelatedProducts({
  allProducts,
  currentCategoryId,
  currentProductId,
}: RelatedProductsProps) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [productRatings, setProductRatings] = useState<Record<string, string>>(
    {}
  );

  // Fetch overall ratings once on mount
  useEffect(() => {
    axiosGetOverallProductRatings((data: any[]) => {
      const map: Record<string, string> = {};
      data.forEach((item) => {
        if (item.product_id && item.average_rating) {
          map[item.product_id] = parseFloat(item.average_rating).toFixed(1);
        }
      });
      setProductRatings(map);
    });
  }, []);

  // Filter: same category, not current product, visibility = 1
  const relatedProducts = allProducts.filter(
    (p) =>
      p.category_id === currentCategoryId &&
      p.id !== currentProductId &&
      Number(p.visibility) === 1
  );

  // Hide component if no related products
  if (relatedProducts.length === 0) return null;

  // Safely extract first image
  const getFirstImageUrl = (imageurl: string | string[]): string => {
    if (Array.isArray(imageurl)) {
      return imageurl[0]?.trim() || "/placeholder.svg";
    }
    return imageurl?.split(",")[0]?.trim() || "/placeholder.svg";
  };

  const getRating = (productId: string) => productRatings[productId] ?? "0.0";

  return (
    <div className="mt-12 relative">
      <h2 className="text-2xl font-semibold mb-6">Related Products</h2>

      {/* Navigation Buttons */}
      <button
        ref={prevRef}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
        aria-label="Previous"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          stroke="black"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        ref={nextRef}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
        aria-label="Next"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          stroke="black"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      <Swiper
        modules={[Navigation, Autoplay]}
        spaceBetween={16}
        loop={true}
        slidesPerView={2}
        autoplay={{ delay: 2500, disableOnInteraction: false }}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        onSwiper={(swiper) => {
          if (swiper.params.navigation && typeof swiper.params.navigation !== "boolean") {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.navigation.init();
            swiper.navigation.update();
          }
        }}
        breakpoints={{
          640: { slidesPerView: 2 },
          1024: { slidesPerView: 3 },
          1280: { slidesPerView: 4 },
        }}
        className="w-full"
      >
        {relatedProducts.map((product) => {
          const rating = getRating(product.id);
          const hasRating = Number(rating) > 0;

          return (
            <SwiperSlide key={product.id}>
              <Link href={`/p/${encodeURIComponent(product.seo_url)}`} aria-label={product.name}>
                <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 rounded-lg">
                  <div className="relative w-full aspect-[4/5] bg-gray-100">
                    <img
                      src={`${assets}${getFirstImageUrl(product.imageurl)}`}
                      alt={product.name}
                      className="rounded-t-lg w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/ecomsaas-assets/images/no-image.webp";
                      }}
                    />
                    {hasRating && (
                      <span className="absolute sm:bottom-3 bottom-1 sm:left-3 left-1 text-pr_combo_text bg-pr_combo_bg text-xs px-2 py-1 rounded-full flex items-center gap-1 z-10">
                        Best Seller |
                        <div className="flex items-center">
                          <Star className="w-3 h-3 fill-yellow-500 text-yellow-500 mr-1" />
                          {rating}
                        </div>
                      </span>
                    )}
                  </div>

                  <CardContent className="p-4 text-left">
                    <div className="mb-1">
                      <h2 className="text-[14px] font-semibold text-category_name truncate">
                        {product.category_name}
                      </h2>
                      <h2 className="text-[14px] font-semibold text-pr_eng_name truncate">
                        {product.name}
                      </h2>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="space-x-[6px]">
                        <span className="text-[16px] font-bold text-black">
                          {getCurrencySymbol()}
                          {product.sp}
                        </span>
                        {product.mrp > product.sp && (
                          <span className="text-[14px] text-[#737E93] line-through">
                            {getCurrencySymbol()}
                            {product.mrp}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}